/**
 * Measure tool — where the lobby's elements are on screen. `collectElementBoxes` reads every rect
 * ONCE; snapping and the alignment check work off that snapshot (reading rects per pointermove
 * would force a layout every frame).
 */

import { viewportSizeOn } from './units';
import type { MeasureAxis } from './units';

export const MEASURE_ROOT_ID = 'qa-measure-overlay-root';
/** Our own overlays — never measured, never snapped to. */
const OWN_UI_ROOT_IDS = [MEASURE_ROOT_ID, 'qa-figma-overlay-root', 'react-lobby-qa-panel-overlay'];
/** Smaller elements are icon / text fragments — noise for alignment. */
const MIN_BOX_SIZE = 4;
/** An edge this close to a guide counts as ON it (sub-pixel rounding). */
export const ALIGNED_TOLERANCE = 0.5;
/** Edges this close but not on the guide are flagged as near-misses. */
const NEAR_MISS_RANGE = 3;
/** Boxes covering most of the viewport are layout wrappers (page, row, track) — they line up with everything. */
const WRAPPER_SIZE_RATIO = 0.8;

export interface Box {
    left: number;
    top: number;
    right: number;
    bottom: number;
}

/** A position a drag can snap to, and the element it belongs to (null for another guide). */
export interface SnapLine {
    pos: number;
    box: Box | null;
}

function ownUiRoots(): HTMLElement[] {
    return OWN_UI_ROOT_IDS.map((id) => document.getElementById(id)).filter((root): root is HTMLElement => !!root);
}

function isInside(roots: HTMLElement[], element: Element): boolean {
    return roots.some((root) => root.contains(element));
}

export function isOwnUi(element: Element): boolean {
    return isInside(ownUiRoots(), element);
}

/** Topmost lobby element under the point, looking through our own handles. */
export function lobbyElementAt(x: number, y: number): Element | null {
    const roots = ownUiRoots();
    const isPageRoot = (element: Element): boolean => element === document.documentElement || element === document.body;
    return document.elementsFromPoint(x, y).find((element) => !isPageRoot(element) && !isInside(roots, element)) ?? null;
}

export function boxOf(element: Element): Box {
    const { left, top, right, bottom } = element.getBoundingClientRect();
    return { left, top, right, bottom };
}

export function sameBox(a: Box, b: Box): boolean {
    return a.left === b.left && a.top === b.top && a.right === b.right && a.bottom === b.bottom;
}

/** Every on-screen lobby element box. Wrappers sharing a child's exact rect collapse into one outline. */
export function collectElementBoxes(): Box[] {
    const roots = ownUiRoots();
    const seen = new Set<string>();
    const boxes: Box[] = [];
    for (const element of Array.from(document.body.getElementsByTagName('*'))) {
        if (isInside(roots, element)) continue;
        const box = boxOf(element);
        const tooSmall = box.right - box.left < MIN_BOX_SIZE || box.bottom - box.top < MIN_BOX_SIZE;
        const offScreen = box.right < 0 || box.bottom < 0 || box.left > window.innerWidth || box.top > window.innerHeight;
        if (tooSmall || offScreen) continue;
        const key = [box.left, box.top, box.right, box.bottom].map((n) => n.toFixed(1)).join(',');
        if (seen.has(key)) continue;
        seen.add(key);
        boxes.push(box);
    }
    return boxes;
}

/** Start, center and end of a box along one axis. */
function edgesOf(box: Box, axis: MeasureAxis): [start: number, center: number, end: number] {
    return axis === 'x'
        ? [box.left, (box.left + box.right) / 2, box.right]
        : [box.top, (box.top + box.bottom) / 2, box.bottom];
}

/** Boxes overlapping `[from, to]` on the OTHER axis — the ones beside a dragged ruler. */
export function boxesNear(boxes: Box[], axis: MeasureAxis, from: number, to: number): Box[] {
    return boxes.filter((box) => (axis === 'x' ? box.bottom >= from && box.top <= to : box.right >= from && box.left <= to));
}

/** Snap lines on one axis: every box's start and end (plus center if asked), then the guides. */
export function snapLinesOn(axis: MeasureAxis, boxes: Box[], guidePositions: number[], includeCenters: boolean): SnapLine[] {
    const lines: SnapLine[] = guidePositions.map((pos) => ({ pos, box: null }));
    for (const box of boxes) {
        const [start, center, end] = edgesOf(box, axis);
        lines.push({ pos: start, box }, { pos: end, box });
        if (includeCenters) lines.push({ pos: center, box });
    }
    return lines;
}

/** The snap line closest to `position`, or `null` if none is within `maxDistance` px. */
export function nearestSnapLine(lines: SnapLine[], position: number, maxDistance: number): SnapLine | null {
    let nearest: SnapLine | null = null;
    let nearestDistance = maxDistance;
    for (const line of lines) {
        const distance = Math.abs(line.pos - position);
        if (distance <= nearestDistance) {
            nearest = line;
            nearestDistance = distance;
        }
    }
    return nearest;
}

/**
 * For each guide, every box whose start / center / end lies within NEAR_MISS_RANGE px of it — only
 * its closest edge per guide. `offset` is edge − guide; within ±ALIGNED_TOLERANCE means aligned.
 */
export function findAlignedBoxes(
    boxes: Box[],
    guides: ReadonlyArray<{ axis: MeasureAxis; pos: number }>,
): Array<{ box: Box; axis: MeasureAxis; edge: number; offset: number }> {
    const matches = [];
    for (const guide of guides) {
        const maxSize = viewportSizeOn(guide.axis) * WRAPPER_SIZE_RATIO;
        for (const box of boxes) {
            const edges = edgesOf(box, guide.axis);
            if (edges[2] - edges[0] > maxSize) continue;
            const edge = edges.reduce((best, e) => (Math.abs(e - guide.pos) < Math.abs(best - guide.pos) ? e : best));
            const offset = edge - guide.pos;
            if (Math.abs(offset) <= NEAR_MISS_RANGE) matches.push({ box, axis: guide.axis, edge, offset });
        }
    }
    return matches;
}
